import type { ListQuery } from '../../administration/users/userTypes';
import type { CaseStatus } from '../../case-management/cases/caseTypes';

/*
  THE CASE IS CASE MANAGEMENT'S (12 Oct 2026). Its status words and the five member flags are the
  registry's, re-exported so every evidence surface keeps reading them from one place.
*/
export {
  ALL_FLAGS,
  CASE_STATUS,
  CASE_STATUS_LABEL,
  FLAG,
  hasFlag,
  normaliseFlags,
} from '../../case-management/cases/caseTypes';
export type EvidenceCaseStatus = CaseStatus;

/*
  THE WIRE ENUMS ARE NUMERIC — no string converter is configured — so each is an `as const`
  object of numbers mirroring the backend member for member. A member added there is added here
  in the same change, or it falls through every label to the default and is described wrongly.
*/

/**
 * `EvidenceItemKind` — the custody FORM, a shelf or a file, snapshot from the kind's "stored as a
 * file" flag at intake. The kind's NAME is the catalog's (`kindName`); this is what it decided.
 */
export const ITEM_KIND = { physical: 0, digital: 1 } as const;
export type EvidenceItemKind = (typeof ITEM_KIND)[keyof typeof ITEM_KIND];

/** `EvidenceItemStatus` — whether the bureau still holds it. Moved only by a custody line. */
export const CUSTODY = { stored: 0, transferred: 1, disposed: 2, forEvaluation: 3, deferred: 4 } as const;
export type EvidenceItemStatus = (typeof CUSTODY)[keyof typeof CUSTODY];

export const CUSTODY_LABEL: Record<EvidenceItemStatus, string> = {
  0: 'Stored',
  1: 'Transferred',
  2: 'Disposed',
  3: 'For evaluation',
  4: 'Deferred',
};

/** The FACET value of each custody state — the server emits and parses the enum NAMES. */
export const CUSTODY_FACET: Record<EvidenceItemStatus, string> = {
  0: 'Stored',
  1: 'Transferred',
  2: 'Disposed',
  3: 'ForEvaluation',
  4: 'Deferred',
};

/** `EvidenceMovementType`. */
export const MOVEMENT = { storedIn: 0, changeOfStorage: 1, transfer: 2, disposal: 3, received: 4, deferred: 5 } as const;
export type EvidenceMovementType = (typeof MOVEMENT)[keyof typeof MOVEMENT];

/** The user's own words — "stored in, pulled out (transfer/disposal), change of storage". */
export const MOVEMENT_LABEL: Record<EvidenceMovementType, string> = {
  0: 'Stored in',
  1: 'Change of storage',
  2: 'Pulled out — transfer',
  3: 'Pulled out — disposal',
  4: 'Received',
  5: 'Deferred',
};

/** `EvidenceAccessAction`. */
export const ACCESS_ACTION_LABEL: Record<number, string> = {
  0: 'Opened the case',
  1: 'Opened the item',
  2: 'Downloaded the evidence file',
  3: 'Downloaded a custody document',
  4: 'Download refused — file no longer matches its fingerprint',
};

// ── Cases, as the Evidence page lists them ──────────────────────────────────────────────

/** The registry's headline plus what the case holds. Registering a case is `CaseListItem`'s. */
export interface EvidenceCaseListItem {
  id: string;
  caseNumber: string;
  /** `********` on a restricted row — fixed length, so it says nothing about the title it hides. */
  title: string;
  status: EvidenceCaseStatus;
  accessLevelId: string | null;
  accessLevelName: string | null;
  accessLevelRank: number | null;
  isRestricted: boolean;
  /** The caller's flags on this case — `FLAG`. Resolved server-side for the caller. */
  permissions: number;
  itemCount: number;
  /** Live items not disposed of — what the server's delete refusal counts. */
  heldCount: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** Whether the caller's account is linked to an employee — the module's employee rule. */
export interface EvidenceEligibility {
  isEmployee: boolean;
  /** The caller's OWN live personnel record — the receive form's default custodian. Absent when not an employee. */
  personnelId?: string | null;
}

export interface EvidenceCaseListQuery extends ListQuery {
  caseStatus?: string;
}

export interface EvidenceAccessLogItem {
  id: string;
  evidenceItemId: string | null;
  inventoryNumber: string | null;
  action: number;
  detail: string | null;
  clientIp: string | null;
  createdAt: string;
  createdByName: string | null;
}

// ── Storage locations ───────────────────────────────────────────────────────────────────

export interface EvidenceStorageLocationListItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface EvidenceStorageLocationOption {
  id: string;
  code: string;
  name: string;
}

export interface EvidenceStorageLocationRequest {
  name: string;
  description: string | null;
}

// ── Items ───────────────────────────────────────────────────────────────────────────────

export interface EvidenceItemListItem {
  id: string;
  evidenceCaseId: string;
  caseNumber: string;
  inventoryNumber: string;
  kind: EvidenceItemKind;
  /** ABSENT on a restricted row, with its name — a picker-added kind can say what the case holds. */
  evidenceKindId: string | null;
  kindName: string | null;
  /** ABSENT on a restricted row — what an item IS says what the case is about. */
  evidenceTypeId: string | null;
  typeName: string | null;
  /** ABSENT on a restricted row. An instant. */
  acquiredAt: string | null;
  /** `********` on a restricted row. */
  description: string;
  /** "Acquired by". ABSENT on a restricted row — an id is what resolves to a name. */
  agentPersonnelId: string | null;
  /** "Agent" — who submitted it at receiving. ABSENT on a restricted row. */
  submittingAgentPersonnelId: string | null;
  /** The receiving event it arrived in — the Received folder's group. */
  receivingId?: string | null;
  /** That receiving's transaction number — `EMR-{yy}{NNN}`. */
  receivingNumber?: string | null;
  /** The marking — ABSENT on a restricted row. */
  purpose: string | null;
  /** The container it was received in. ABSENT on a restricted row. */
  containerNumber: string | null;
  /** TRANSFER DETAIL — the latest line out of the facility: where it went, and why. Null while inside. */
  transferredTo: string | null;
  transferReason: string | null;
  /** THE EVALUATION AUDIT, as ENTERED — instants, and employees (ids ABSENT on a restricted row). Null until evaluated. */
  evaluatedAt: string | null;
  evaluatedByPersonnelId: string | null;
  storedAt: string | null;
  storedByPersonnelId: string | null;
  /** The acknowledgement receipt listing it, or null. ABSENT on a restricted row. */
  receiptNumber?: string | null;
  custodianPersonnelId: string | null;
  storageLocationId: string | null;
  storageLocationCode: string | null;
  storageLocationName: string | null;
  status: EvidenceItemStatus;
  pendingMovementType: EvidenceMovementType | null;
  /** Custody lines written. Above 1 the item has moved, and a stored item then cannot be deleted. */
  movementCount: number;
  fileName: string | null;
  fileSizeBytes: number | null;
  isRestricted: boolean;
  /** The CASE's flags for the caller. */
  permissions: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface EvidenceMovementItem {
  id: string;
  sequence: number;
  movementType: EvidenceMovementType;
  fromLocationName: string | null;
  toLocationName: string | null;
  fromCustodianPersonnelId: string | null;
  toCustodianPersonnelId: string | null;
  transferredTo: string | null;
  reason: string;
  /** Null on a first stored-in, which records none. */
  justification: string | null;
  movementRequestId: string | null;
  passwordConfirmed: boolean;
  recordedAt: string;
  recordedByName: string | null;
  hash: string;
  attachmentCount: number;
}

export interface EvidenceAttachmentItem {
  id: string;
  evidenceMovementId: string;
  movementSequence: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  createdAt: string;
  createdByName: string | null;
}

export interface EvidencePendingMovement {
  id: string;
  movementType: EvidenceMovementType;
  toLocationName: string | null;
  toCustodianPersonnelId: string | null;
  transferredTo: string | null;
  reason: string;
  /** Null on a line raised by a transfer batch. */
  justification: string | null;
  /** The batch it rides in — one approval decides every item. */
  transferRequestId?: string | null;
  requestedAt: string;
  requestedByName: string | null;
}

export interface EvidenceItemDetail {
  /** The latest court or disposal batch this item rode in, whatever was decided. Absent if none. */
  transfer?: EvidenceTransferDetail | null;
  item: EvidenceItemListItem;
  fileContentType: string | null;
  fileSha256: string | null;
  movements: EvidenceMovementItem[];
  attachments: EvidenceAttachmentItem[];
  pendingMovement: EvidencePendingMovement | null;
}

export interface EvidenceChainVerification {
  intact: boolean;
  lineCount: number;
  brokenAtSequence: number | null;
  headHash: string | null;
  verifiedAt: string;
}

export interface EvidenceItemListQuery extends ListQuery {
  kind?: string;
  type?: string;
  /** ONE container, exact — an Evidence-tab group. '' is "No container". A scope, not a facet. */
  container?: string;
  custody?: string;
  location?: string;
  /** A SCOPE, never a facet — the case profile's Evidence tab. */
  caseId?: string;
  /** Box by box, "No container" last — the Stored in folder's grouped table. Overrides the sort. */
  groupByContainer?: boolean;
  /** Receiving event by event, newest first — the Received folder's grouped table. Overrides the sort. */
  groupByReceiving?: boolean;
}

/** One receiving event — the header the items share, and one row per item. All or nothing. */
export interface StoreEvidenceBatchRequest {
  evidenceCaseId: string;
  custodianPersonnelId: string;
  /** THE AGENT submitting this evidence. Not "acquired by", which is per row. */
  submittingAgentPersonnelId: string;
  /** NO location and NO container — both are assigned at EVALUATION, when an item is accepted. */
  rows: StoreEvidenceRow[];
  /** The pending RECEIVING request this receive fulfils, or absent. */
  evidenceRequestId?: string | null;
}

/** One item. NO kind: the server derives it — an evidence file attached makes it digital. */
export interface StoreEvidenceRow {
  evidenceTypeId: string;
  description: string;
  /** An ISO instant. */
  acquiredAt: string;
  /** "Acquired by" — who actually acquired this item. */
  agentPersonnelId: string;
  /** The request line this row came from — its documents ride onto the item's first custody line. */
  requestLineId?: string | null;
}

/** A row's files: the evidence itself (a file kind only) and its supporting documents. */
export interface EvidenceRowFiles {
  file: File | null;
  attachments: File[];
}

/** One container of a case — an Evidence-tab group header. A null number is "No container". */
export interface EvidenceContainerGroup {
  containerNumber: string | null;
  itemCount: number;
  /** The box's custodian and agent when every item agrees; absent where they differ. */
  custodianPersonnelId?: string | null;
  submittingAgentPersonnelId?: string | null;
  /** The caller is the custodian of every item in it — only then may they edit or delete it. */
  canManage: boolean;
  /** Why a Delete would be refused (an item pending approval or on a receipt), or absent. */
  deleteBlockedReason?: string | null;
  /** The receiving transactions (EMR-…) its items came in — a box may hold several. */
  receivingNumbers?: string[] | null;
}

/** One receiving event among a case's waiting items — a Received folder group header. */
export interface EvidenceReceivingGroup {
  receivingId: string;
  /** `EMR-{yy}{NNN}`. */
  receivingNumber?: string | null;
  /** An instant — when the intake was recorded. */
  receivedAt: string;
  /** Its items still waiting for evaluation. */
  itemCount: number;
  custodianPersonnelId?: string | null;
  submittingAgentPersonnelId?: string | null;
  /** Its waiting items — the header's Acknowledgement receipt lists exactly these, receipted or not. */
  receiptableItemIds?: string[];
}

export interface UpdateEvidenceContainerRequest {
  evidenceCaseId: string;
  containerNumber: string | null;
  custodianPersonnelId: string;
  submittingAgentPersonnelId: string;
}

/** `EvidenceEvaluationDecision`. */
export const EVALUATION = { accept: 0, defer: 1 } as const;
export type EvidenceEvaluationDecision = (typeof EVALUATION)[keyof typeof EVALUATION];

/** Several items, ONE decision, all or nothing. */
export interface EvaluateEvidenceRequest {
  itemIds: string[];
  decision: EvidenceEvaluationDecision;
  /** Accept only. */
  containerNumber: string | null;
  /** Accept only. */
  storageLocationId: string | null;
  /** Defer only. */
  reason: string | null;
  /** Defer only — where it is kept outside the facility. */
  place: string | null;
  /** THE AUDIT, entered — required. Instants are ISO. */
  evaluatedByPersonnelId: string;
  evaluatedAt: string;
  storedByPersonnelId: string;
  storedAt: string;
}

export interface EvidenceBatchResult {
  /** The receiving's transaction number — `EMR-{yy}{NNN}`. */
  receivingNumber: string;
  items: { id: string; inventoryNumber: string }[];
}

export interface StoreInEvidenceItemRequest {
  custodianPersonnelId: string;
  storageLocationId: string | null;
  reason: string;
  justification: string;
}

export interface EvidenceItemRequest {
  description: string;
  containerNumber: string;
  /** "Acquired by". */
  agentPersonnelId: string;
  evidenceTypeId: string;
  /** An ISO instant. */
  acquiredAt: string;
}

/** An item's marking — its purpose. The request REPLACES it. */
export interface EvidenceItemMarkingsRequest {
  purpose: string | null;
}

// ── Kinds and types ─────────────────────────────────────────────────────────────────────

export interface EvidenceKindListItem {
  id: string;
  name: string;
  /** Whether evidence of this kind IS a file — decides the intake's Evidence file column. */
  storesFile: boolean;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface EvidenceKindOption {
  id: string;
  name: string;
  storesFile: boolean;
}

export interface EvidenceKindRequest {
  name: string;
  storesFile: boolean;
  description: string | null;
}

export interface EvidenceTermListItem {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface EvidenceTermOption {
  id: string;
  name: string;
}

export interface EvidenceTermRequest {
  name: string;
  description: string | null;
}

export interface RequestEvidenceMovementRequest {
  movementType: EvidenceMovementType;
  toLocationId: string | null;
  toCustodianPersonnelId: string | null;
  transferredTo: string | null;
  reason: string;
  justification: string;
}

// ── File types ──────────────────────────────────────────────────────────────────────────

export interface EvidenceFileTypeItem {
  id: string;
  contentType: string;
  name: string;
  extensions: string[];
  isEnabled: boolean;
  maxSizeBytes: number;
  ceilingBytes: number;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface UpdateEvidenceFileTypeRequest {
  isEnabled: boolean;
  maxSizeBytes: number;
}

/** The cap on documents filed with one custody line. Mirrors `EvidenceItemFieldRules.MaxAttachments`. */
export const MAX_ATTACHMENTS = 5;

/** The most items one receiving event carries. Mirrors `EvidenceItemFieldRules.MaxRows`. */
export const MAX_INTAKE_ROWS = 50;

/**
 * Every extension the code can recognise as evidence — `EvidenceFileTypes` on the server. A hint
 * for the browser's file dialog only: which of them is switched on, and how large each may be, is
 * the server's setting, and the bytes are verified there by their signature.
 */
export const EVIDENCE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.mp4', '.mp3', '.wav', '.zip', '.docx', '.xlsx'];

/** The toast a receiving event earns — the numbers it was given, or how many when there are many. */
/** The success line — the receiving's TRANSACTION NUMBER first (09 Oct 2026), then what it holds. */
export function receivedMessage(receivingNumber: string, numbers: string[]): string {
  if (numbers.length === 1) return `${receivingNumber}: ${numbers[0]} was received.`;
  if (numbers.length <= 3) return `${receivingNumber}: ${numbers.join(', ')} were received.`;
  return `${receivingNumber}: ${numbers.length} items were received, ${numbers[0]} to ${numbers[numbers.length - 1]}.`;
}

// ── Acknowledgement receipts ─────────────────────────────────────────────────────────────

/** One item on a receipt — what it is, by the numbers the case already prints. */
export interface EvidenceReceiptLine {
  evidenceItemId: string;
  inventoryNumber: string;
  typeName: string;
  description: string;
}

/** A receipt. Permanent — never edited or deleted. People are personnel ids, resolved on the page. */
export interface EvidenceReceiptListItem {
  id: string;
  evidenceCaseId: string;
  /** `EMAR-{yy}{NNNN}`. */
  receiptNumber: string;
  receivedFromPersonnelId: string;
  receivedByPersonnelId: string;
  /** An instant, as entered. */
  receivedAt: string;
  items: EvidenceReceiptLine[];
  createdAt: string;
  createdByName: string | null;
}

/** No number — the server allocates it. */
export interface CreateEvidenceReceiptRequest {
  evidenceCaseId: string;
  receivedFromPersonnelId: string;
  receivedByPersonnelId: string;
  /** ISO instant. */
  receivedAt: string;
  itemIds: string[];
}

// ── Transfer (the Stored folder's) ──────────────────────────────────────────────────────

/** `EvidenceTransferDestination` — numeric on the wire. */
export const DESTINATION = { court: 0, disposal: 1, defer: 2 } as const;
export type EvidenceTransferDestination = (typeof DESTINATION)[keyof typeof DESTINATION];

export const DESTINATION_LABEL: Record<EvidenceTransferDestination, string> = {
  0: 'Court',
  1: 'Disposed',
  2: 'Defer to other storage',
};

/** Multipart; the court order's file travels beside it as `courtOrder`. */
export interface TransferEvidenceRequest {
  evidenceCaseId: string;
  destination: EvidenceTransferDestination;
  itemIds: string[];
  courtBranch: string | null;
  courtOrderNumber: string | null;
  /** The judge's name — written to the identity store; nothing in rsys_ keeps it. */
  /** A TITLE — Mr., Dr., Atty. Optional; mirrored in lib/salutations.ts. */
  judgeSalutation: string | null;
  judgeFirstName: string | null;
  judgeMiddleName: string | null;
  judgeLastName: string | null;
  judgeSuffix: string | null;
  /** Court — the employee requesting it. */
  requestorPersonnelId: string | null;
  evidenceDisposalTypeId: string | null;
  reason: string | null;
  place: string | null;
  /** The pending TRANSFER request this fulfils. Its judge and order file carry over when none are sent. */
  evidenceRequestId?: string | null;
}

export interface EvidenceTransferResult {
  transferRequestId?: string | null;
  itemCount: number;
  /** True while it waits for approval — nothing has moved yet. */
  pending: boolean;
}

/** A batch's facts on an item's page. The judge is a PersonId — the page resolves the name. */
export interface EvidenceTransferDetail {
  destination: EvidenceTransferDestination;
  /** `EvidenceMovementRequestStatus`: 0 pending · 1 approved · 2 rejected · 3 retracted. */
  status: number;
  courtBranch?: string | null;
  courtOrderNumber?: string | null;
  judgePersonId?: string | null;
  requestorPersonnelId?: string | null;
  disposalTypeName?: string | null;
  reason?: string | null;
  itemCount: number;
  requestedAt: string;
  requestedByName?: string | null;
}

// ── Evidence requests (Self Service raises them; the case's team acts on them) ─────────────

/** `EvidenceRequestKind` — numeric on the wire. The LABEL is the server's (`kindLabel`). */
export const REQUEST_KIND = { receiving: 0, transfer: 1 } as const;
export type EvidenceRequestKind = (typeof REQUEST_KIND)[keyof typeof REQUEST_KIND];

/** `EvidenceRequestStatus` — numeric on the wire. The LABEL is the server's (`statusLabel`). */
export const REQUEST_STATUS = { pending: 0, fulfilled: 1, declined: 2, cancelled: 3 } as const;
export type EvidenceRequestStatus = (typeof REQUEST_STATUS)[keyof typeof REQUEST_STATUS];

export interface EvidenceRequestAttachmentItem {
  id: string;
  fileName: string;
  sizeBytes: number;
}

/** One line — a receiving line carries what the requester typed; a transfer line the item. */
export interface EvidenceRequestLineItem {
  id: string;
  lineNumber: number;
  evidenceTypeId?: string | null;
  typeName?: string | null;
  /** A transfer line's description is withheld (`********`) when the reader cannot open the case. */
  description?: string | null;
  acquiredAt?: string | null;
  acquiredByPersonnelId?: string | null;
  evidenceItemId?: string | null;
  inventoryNumber?: string | null;
  containerNumber?: string | null;
  storageLocationName?: string | null;
  attachments: EvidenceRequestAttachmentItem[];
}

/** A request whole. People are ids — the requester and each acquirer PersonnelIds, the judge a PersonId. */
export interface EvidenceRequestDetail {
  id: string;
  /** `ERQ-{yy}{NNNN}`. */
  requestNumber: string;
  kind: EvidenceRequestKind;
  kindLabel: string;
  status: EvidenceRequestStatus;
  statusLabel: string;
  evidenceCaseId: string;
  caseNumber: string;
  /** `********` when the reader cannot open the case today. */
  caseTitle: string;
  isRestricted: boolean;
  requesterPersonnelId: string;
  destination?: EvidenceTransferDestination | null;
  courtBranch?: string | null;
  courtOrderNumber?: string | null;
  judgePersonId?: string | null;
  courtOrderFile?: string | null;
  evidenceDisposalTypeId?: string | null;
  disposalTypeName?: string | null;
  reason?: string | null;
  declineReason?: string | null;
  decidedAt?: string | null;
  decidedByName?: string | null;
  /** The receiving a fulfilled receiving request became — `EMR-{yy}{NNN}`. */
  receivingNumber?: string | null;
  transferRequestId?: string | null;
  lines: EvidenceRequestLineItem[];
  isActive: boolean;
  createdAt: string;
  createdBy?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** The requester's register row. */
export interface EvidenceRequestListItem {
  id: string;
  requestNumber: string;
  kind: EvidenceRequestKind;
  kindLabel: string;
  status: EvidenceRequestStatus;
  statusLabel: string;
  evidenceCaseId: string;
  caseNumber: string;
  caseTitle: string;
  isRestricted: boolean;
  requesterPersonnelId: string;
  destination?: EvidenceTransferDestination | null;
  lineCount: number;
  isActive: boolean;
  createdAt: string;
  createdBy?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** A case the caller may raise a request on — a member, cleared, live. */
export interface EvidenceRequestCaseOption {
  id: string;
  caseNumber: string;
  title: string;
}

/** A stored item a transfer request may name — the fields the Transfer form's picker and table read. */
export interface EvidenceRequestableItem {
  id: string;
  inventoryNumber: string;
  typeName?: string | null;
  description: string;
  containerNumber?: string | null;
  storageLocationName?: string | null;
}

/** Multipart; each row's documents travel as `rows[i].attachments`. No requester field — it is the caller. */
export interface RequestEvidenceReceivingRequest {
  evidenceCaseId: string;
  rows: {
    evidenceTypeId: string;
    description: string;
    /** An ISO instant. */
    acquiredAt: string;
    acquiredByPersonnelId: string;
  }[];
}

/** Multipart; the court order's file travels beside it as `courtOrder`. Court or disposal — never a deferral. */
export interface RequestEvidenceTransferRequest {
  evidenceCaseId: string;
  destination: EvidenceTransferDestination;
  itemIds: string[];
  courtBranch: string | null;
  courtOrderNumber: string | null;
  /** A TITLE — Mr., Dr., Atty. Optional; mirrored in lib/salutations.ts. */
  judgeSalutation: string | null;
  judgeFirstName: string | null;
  judgeMiddleName: string | null;
  judgeLastName: string | null;
  judgeSuffix: string | null;
  evidenceDisposalTypeId: string | null;
  reason: string | null;
}

export interface EvidenceRequestResult {
  id: string;
  requestNumber: string;
}
